import { useState } from 'react';
import { Alert, Grid, Typography, Box, MenuItem, Select, CircularProgress, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { StyledInput, Label, grey, GlassCard, SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RelationalAutocomplete from '../RelationalAutocomplete';

const SectionTitle = styled(Typography)(() => ({
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
}));

const ServiceBadge = styled(Box)(() => ({
    display: 'inline-block',
    padding: '6px 16px',
    borderRadius: '12px',
    backgroundColor: alpha('#3DAC5A', 0.1),
    color: '#3DAC5A',
    fontWeight: 700,
    fontSize: '0.85rem',
}));

const CustomSelect = styled(({ MenuProps, ...props }) => (
    <Select MenuProps={{ ...SELECTED_MENU_PROPS, ...MenuProps }} {...props} />
))(({ theme }) => ({
    width: '100%',
    height: '40px',
    backgroundColor: theme.palette.mode === 'dark' ? alpha(grey[900], 0.4) : alpha('#fff', 0.6),
    backdropFilter: 'blur(8px)',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.mode === 'dark' ? alpha(grey[700], 0.5) : alpha(grey[200], 0.5)}`,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    boxShadow: `0 4px 6px ${alpha('#000', 0.02)}`,
    transition: 'all 0.2s ease',
    '&:hover': {
        border: '1px solid #3DAC5A',
    },
    '&.Mui-focused': {
        border: '1px solid #3DAC5A',
        backgroundColor: '#fff',
    }
}));

const JobTab = ({
    formData,
    handleInputChange,
    handleDateChange,
    masterData,
    loadingMaster,
    masterDataError = '',
    identifierErrors = {},
}) => {
    const {
        managers = [], branches = [], locations = [], teamHeads = [],
        departments = [], designations = [],
    } = masterData || {};

    // Sentinel dropdown value + dialog state for creating a custom attendance
    // schema on the fly. The schema is free text (the backend parses the start
    // time out of it for late/OT), so a custom string just needs to be readable.
    const ADD_SCHEMA = '__add_schema__';
    const [addSchemaOpen, setAddSchemaOpen] = useState(false);
    const [newSchema, setNewSchema] = useState('');

    const handleManagerChange = (selected) => {
        handleInputChange({ target: { name: 'reporting_manager_id', value: selected?.id || null } });
        handleInputChange({ target: { name: 'reporting_manager', value: selected?.name || '' } });
        handleInputChange({ target: { name: 'manager_id', value: selected?.employee_no || '' } });
    };

    const handleSchemaChange = (e) => {
        if (e.target.value === ADD_SCHEMA) {
            setNewSchema('');
            setAddSchemaOpen(true);
            return; // don't write the sentinel into formData
        }
        handleInputChange(e);
    };

    const handleAddSchema = () => {
        const val = newSchema.trim();
        if (!val) return;
        handleInputChange({ target: { name: 'attendance_schema', value: val } });
        setAddSchemaOpen(false);
    };

    const handleDepartmentChange = (selected) => {
        const selectedId = selected?.id || null;
        const selectedName = selected?.name || '';
        const sameDepartment = formData.department_id
            ? String(formData.department_id) === String(selectedId)
            : String(formData.department || '').trim().toLocaleLowerCase()
                === String(selectedName).trim().toLocaleLowerCase();

        handleInputChange({ target: { name: 'department_id', value: selectedId } });
        handleInputChange({ target: { name: 'department', value: selectedName } });
        if (!sameDepartment) {
            handleInputChange({ target: { name: 'designation_id', value: null } });
            handleInputChange({ target: { name: 'designation', value: '' } });
        }
    };

    const handleDesignationChange = (selected) => {
        handleInputChange({ target: { name: 'designation_id', value: selected?.id || null } });
        handleInputChange({ target: { name: 'designation', value: selected?.name || '' } });
    };

    const handleBranchChange = (selected) => {
        handleInputChange({ target: { name: 'branch_id', value: selected?.id || null } });
        handleInputChange({ target: { name: 'branch_name', value: selected?.name || '' } });
    };

    const availableDesignations = designations.filter(
        (item) => String(item.department) === String(formData.department_id)
    );
    const availableManagers = managers.filter(
        (item) => String(item.employee_no) !== String(formData.employee_no)
    );

    // Helper to ensure the current value is always an option (prevents MUI warnings).
    // A legacy value that doesn't match any master-list entry is surfaced right
    // after the placeholder — not buried at the bottom of the list — with a
    // disabled hint line explaining why. The hint is its OWN MenuItem, never the
    // selectable one: MUI's Select renders the selected MenuItem's children
    // verbatim in the closed box, so if the explanation lived inside the
    // selectable item itself, it would leak into the field's display text too.
    // `masterOnly` (Branch): the legacy value is still shown so HR can see what
    // is stored, but it is disabled — HR can only pick a master entry. A
    // non-master branch hides the employee from branch-scoped HR (HRP #55718).
    const renderSafeOptions = (options, currentValue, placeholder, masterOnly = false) => {
        const hasValue = options.some(opt => (opt.name || opt) === currentValue);
        return [
            <MenuItem key="placeholder" value="" disabled>{placeholder}</MenuItem>,
            currentValue && !hasValue && (
                <MenuItem key="fallback-hint" disabled sx={{ fontSize: '0.72rem', color: masterOnly ? 'error.main' : 'text.secondary' }}>
                    {masterOnly ? 'Not an official branch — please choose one below:' : 'Current value (not in master list):'}
                </MenuItem>
            ),
            currentValue && !hasValue && (
                <MenuItem key="fallback" value={currentValue} disabled={masterOnly}>{currentValue}</MenuItem>
            ),
            ...options.map((opt, i) => (
                <MenuItem key={i} value={opt.name || opt}>{opt.name || opt}</MenuItem>
            )),
        ];
    };

    const calculateService = (doj) => {
        if (!doj) return 'N/A';
        const start = dayjs(doj);
        const now = dayjs();
        const years = now.diff(start, 'year');
        const months = now.diff(start, 'month') % 12;
        return `${years}Y, ${months}M`;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {masterDataError && <Alert severity="error">{masterDataError}</Alert>}
            <GlassCard sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                    <Box>
                        <SectionTitle sx={{ mb: 1 }}>
                            <WorkIcon sx={{ color: '#3DAC5A' }} />
                            Job Details
                        </SectionTitle>
                        <Typography variant="body2" color="text.secondary">
                            Professional role and structural placement within the organization.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {loadingMaster && <CircularProgress size={20} color="success" />}
                        <ServiceBadge>
                            Service: {calculateService(formData.date_of_joining)}
                        </ServiceBadge>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <Label>Date of Joining</Label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={formData.date_of_joining ? dayjs(formData.date_of_joining) : null}
                                onChange={(val) => handleDateChange('date_of_joining', val)}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                height: '40px',
                                                borderRadius: '10px',
                                                bgcolor: alpha('#fff', 0.6),
                                                '& fieldset': { border: `1px solid ${alpha(grey[200], 0.5)}` },
                                                '&:hover fieldset': { borderColor: '#3DAC5A' },
                                                '&.Mui-focused fieldset': { borderColor: '#3DAC5A' },
                                            }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Department</Label>
                        <RelationalAutocomplete
                            ariaLabel="Department"
                            options={departments}
                            valueId={formData.department_id}
                            legacyLabel={formData.department}
                            onChange={handleDepartmentChange}
                            loading={loadingMaster}
                            disabled={loadingMaster || departments.length === 0}
                            placeholder="Search Department"
                            emptyText="No active departments configured"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Reporting Manager</Label>
                        <RelationalAutocomplete
                            ariaLabel="Reporting Manager"
                            options={availableManagers}
                            valueId={formData.reporting_manager_id}
                            legacyLabel={formData.reporting_manager}
                            onChange={handleManagerChange}
                            loading={loadingMaster}
                            disabled={loadingMaster || availableManagers.length === 0}
                            placeholder="Search Reporting Manager"
                            emptyText="No active employees available"
                            getOptionLabel={(manager) => `${manager.name} (${manager.employee_no})`}
                        />
                        {!loadingMaster && availableManagers.length === 0 && !masterDataError && (
                            <Typography variant="caption" color="text.secondary">
                                No active employee is available to assign as manager.
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Confirmation Date</Label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={formData.confirmation_date ? dayjs(formData.confirmation_date) : null}
                                onChange={(val) => handleDateChange('confirmation_date', val)}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                height: '40px',
                                                borderRadius: '10px',
                                                bgcolor: alpha('#fff', 0.6),
                                                '& fieldset': { border: `1px solid ${alpha(grey[200], 0.5)}` },
                                                '&:hover fieldset': { borderColor: '#3DAC5A' },
                                                '&.Mui-focused fieldset': { borderColor: '#3DAC5A' },
                                            }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Employment Status *</Label>
                        <CustomSelect
                            name="employment_status"
                            value={formData.employment_status || ''}
                            onChange={handleInputChange}
                            displayEmpty
                        >
                            {renderSafeOptions(['Confirmed', 'Probation'], formData.employment_status, 'Select Status')}
                        </CustomSelect>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Branch *</Label>
                        <RelationalAutocomplete
                            ariaLabel="Branch"
                            options={branches}
                            valueId={formData.branch_id}
                            legacyLabel={formData.branch_name}
                            onChange={handleBranchChange}
                            loading={loadingMaster}
                            disabled={loadingMaster || branches.length === 0}
                            placeholder="Search Branch"
                            emptyText="No branches configured"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Region / Location *</Label>
                        <CustomSelect
                            name="location"
                            value={formData.location || ''}
                            onChange={handleInputChange}
                            displayEmpty
                        >
                            {renderSafeOptions(locations, formData.location, 'Select Location')}
                        </CustomSelect>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Division</Label>
                        <StyledInput
                            name="division"
                            value={formData.division || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Designation</Label>
                        <RelationalAutocomplete
                            ariaLabel="Designation"
                            options={availableDesignations}
                            valueId={formData.designation_id}
                            legacyLabel={formData.designation}
                            onChange={handleDesignationChange}
                            loading={loadingMaster}
                            disabled={loadingMaster || !formData.department_id || availableDesignations.length === 0}
                            placeholder="Search Designation"
                            emptyText={formData.department_id
                                ? 'No active designations for this department'
                                : 'Select a Department first'}
                        />
                        {!loadingMaster && formData.department_id && !formData.designation_id
                            && !formData.designation && (
                            <Typography variant="caption" color="warning.main">
                                No designation is assigned. Select one from this Department.
                            </Typography>
                        )}
                        {!loadingMaster && formData.department_id
                            && availableDesignations.length === 0 && !masterDataError && (
                            <Typography variant="caption" color="error">
                                No active Designation is configured for this Department in Master Data.
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Status *</Label>
                        <CustomSelect
                            name="status"
                            value={formData.status || ''}
                            onChange={handleInputChange}
                            displayEmpty
                        >
                            {renderSafeOptions(['Active', 'Inactive'], formData.status, 'Select Status')}
                        </CustomSelect>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Attendance Schema *</Label>
                        <CustomSelect
                            name="attendance_schema"
                            value={formData.attendance_schema || ''}
                            onChange={handleSchemaChange}
                            displayEmpty
                        >
                            {renderSafeOptions([
                                '8.30 AM - 5.30 PM',
                                '9.00 AM - 6.30 PM',
                                '8.00 AM - 5.00 PM',
                                '09:00AM - 06:00PM scheme'
                            ], formData.attendance_schema, 'Select Schema')}
                            <MenuItem
                                key="add-schema"
                                value={ADD_SCHEMA}
                                sx={{ color: '#3DAC5A', fontWeight: 700, gap: 1 }}
                            >
                                <AddCircleOutlineIcon fontSize="small" /> Add new schema…
                            </MenuItem>
                        </CustomSelect>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Level Cost Center</Label>
                        <StyledInput
                            name="level_cost_center"
                            value={formData.level_cost_center || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Grade</Label>
                        <StyledInput
                            name="grade"
                            value={formData.grade || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Team Head *</Label>
                        <CustomSelect
                            name="team_head"
                            value={formData.team_head || ''}
                            onChange={handleInputChange}
                            displayEmpty
                        >
                            {renderSafeOptions(teamHeads, formData.team_head, 'Select Team Head')}
                        </CustomSelect>
                    </Grid>
                </Grid>
            </GlassCard>

            <GlassCard sx={{ p: 4 }}>
                <Box sx={{ mb: 4 }}>
                    <SectionTitle sx={{ mb: 1 }}>
                        <AccountBalanceIcon sx={{ color: '#3DAC5A' }} />
                        Bank & ID Details
                    </SectionTitle>
                    <Typography variant="body2" color="text.secondary">
                        Banking information and statutory identification numbers.
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <Label>Bank Account No</Label>
                        <StyledInput
                            name="bank_account_no"
                            value={formData.bank_account_no || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>IFSC Code</Label>
                        <StyledInput
                            name="ifsc_code"
                            value={formData.ifsc_code || ''}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 11, 'aria-invalid': Boolean(identifierErrors.ifsc_code) }}
                            sx={identifierErrors.ifsc_code ? { borderColor: '#ef4444' } : undefined}
                        />
                        {identifierErrors.ifsc_code && (
                            <Typography variant="caption" color="error">{identifierErrors.ifsc_code}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>PAN Card No</Label>
                        <StyledInput
                            name="pan_no"
                            value={formData.pan_no || ''}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 10, 'aria-invalid': Boolean(identifierErrors.pan_no) }}
                            sx={identifierErrors.pan_no ? { borderColor: '#ef4444' } : undefined}
                        />
                        {identifierErrors.pan_no && (
                            <Typography variant="caption" color="error">{identifierErrors.pan_no}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>UAN Number</Label>
                        <StyledInput
                            name="uan_no"
                            value={formData.uan_no || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>ESI Number</Label>
                        <StyledInput
                            name="ESIno"
                            value={formData.ESIno || ''}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Label>Aadhaar Number</Label>
                        <StyledInput
                            name="aadhaar_number"
                            value={formData.aadhaar_number || ''}
                            onChange={handleInputChange}
                            placeholder="12-digit Aadhaar No"
                        />
                    </Grid>
                </Grid>
            </GlassCard>

            {/* Create a custom attendance schema for this employee. zIndex is
                bumped above the fixed admin header (z1301) so the title shows. */}
            <Dialog
                open={addSchemaOpen}
                onClose={() => setAddSchemaOpen(false)}
                maxWidth="xs"
                fullWidth
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>Add Attendance Schema</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter the shift window. The start time is used for late/overtime,
                        so keep it readable, e.g. <b>10.00 AM - 7.00 PM</b>.
                    </Typography>
                    <Label>Schema</Label>
                    <StyledInput
                        autoFocus
                        fullWidth
                        value={newSchema}
                        onChange={(e) => setNewSchema(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddSchema(); }}
                        placeholder="e.g. 10.00 AM - 7.00 PM"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAddSchemaOpen(false)} sx={{ color: grey[600] }}>Cancel</Button>
                    <Button
                        onClick={handleAddSchema}
                        variant="contained"
                        disabled={!newSchema.trim()}
                        sx={{ bgcolor: '#3DAC5A', '&:hover': { bgcolor: '#329049' }, fontWeight: 700 }}
                    >
                        Add Schema
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default JobTab;
